import React, { useState } from "react";
import { useNotify } from "../../components/ConfirmModal";
import { Modal, StatusPill } from "./WorkUi";
import { errorMessage, fmtShortDate } from "./workApi";

const QUICK = [
  { status: "In Progress", label: "Working on it" },
  { status: "In Review", label: "Ready for review" },
  { status: "Blocked", label: "Stuck" },
  { status: "Done", label: "Done" },
];

const rowFor = (item) => ({
  Item_Id: item.Item_Id,
  Status: null,
  Progress_Pct: item.Progress_Pct || 0,
  Note: "",
  Blocker: "",
  Est_Done_Date: item.Est_Done_Date || "",
  Est_Post_Date: item.Planned_Post_Date || "",
});

/** Daily status for all my open items in one screen: status, progress, note, blocker, estimated dates. */
function DailyUpdate({ api, items, generalNote, onClose, onSaved }) {
  const notify = useNotify();
  const [rows, setRows] = useState(() => Object.fromEntries(items.map((i) => [i.Item_Id, rowFor(i)])));
  const [note, setNote] = useState(generalNote || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const set = (id, key, value) => setRows((r) => ({ ...r, [id]: { ...r[id], [key]: value } }));

  const save = async () => {
    for (const item of items) {
      const row = rows[item.Item_Id];
      if (row.Status === "Blocked" && !row.Blocker.trim()) {
        setError(`Tell us what is blocking "${item.Title}".`);
        return;
      }
    }
    setSaving(true);
    setError("");
    try {
      const payload = items.map((item) => {
        const row = rows[item.Item_Id];
        const body = {
          Item_Id: item.Item_Id,
          Progress_Pct: Number(row.Progress_Pct) || 0,
          Note: row.Note.trim() || null,
          Blocker: row.Status === "Blocked" ? row.Blocker.trim() : null,
          Est_Done_Date: row.Est_Done_Date || null,
        };
        if (item.Planned_Post_Date || row.Est_Post_Date) body.Est_Post_Date = row.Est_Post_Date || null;
        if (row.Status && row.Status !== item.Status) body.Status = row.Status;
        return body;
      });
      await api.submitDaily({ items: payload, general_note: note.trim() || null });
      notify.success("Today's update saved. Thanks!");
      onSaved?.();
      onClose();
    } catch (err) {
      setError(errorMessage(err, "Could not save your update"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title="Update my day"
      onClose={onClose}
      wide
      footer={
        <>
          <button type="button" className="dmp-btn dmp-btn--ghost wk-btn-lg" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="dmp-btn wk-btn-lg" onClick={save} disabled={saving}>
            {saving ? "Saving…" : "Save update"}
          </button>
        </>
      }
    >
      <p className="wk-muted" style={{ marginTop: 0 }}>
        Takes a minute. Your lead and manager see this instead of asking you for status.
      </p>
      {error ? <div className="dmp-error">{error}</div> : null}
      {items.length ? (
        items.map((item) => {
          const row = rows[item.Item_Id];
          const current = row.Status || item.Status;
          return (
            <div className="wk-daily-row" key={item.Item_Id}>
              <div className="wk-daily-row__title">
                <span>{item.Title}</span>
                <StatusPill status={item.Status} small />
                {item.Project_Name ? <span className="wk-muted" style={{ fontWeight: 400 }}>{item.Project_Name}</span> : null}
              </div>
              <div className="wk-chip-row" role="group" aria-label={`Status of ${item.Title}`}>
                {QUICK.filter((q) => !(q.status === "Done" && item.Planned_Post_Date)).map((q) => (
                  <button
                    key={q.status}
                    type="button"
                    className="wk-chip"
                    aria-pressed={current === q.status}
                    onClick={() => set(item.Item_Id, "Status", current === q.status ? null : q.status)}
                  >
                    {q.label}
                  </button>
                ))}
              </div>
              {current === "Blocked" ? (
                <label className="wk-field">
                  What is blocking you?
                  <input value={row.Blocker} onChange={(e) => set(item.Item_Id, "Blocker", e.target.value)} placeholder="e.g. Waiting for client photos" />
                </label>
              ) : null}
              <div className="wk-row">
                <label className="wk-field">
                  Progress: {row.Progress_Pct}%
                  <input
                    type="range"
                    min="0"
                    max="100"
                    step="10"
                    value={row.Progress_Pct}
                    onChange={(e) => set(item.Item_Id, "Progress_Pct", e.target.value)}
                    style={{ minHeight: 0, padding: 0, border: "none" }}
                  />
                </label>
                <label className="wk-field">
                  Expected finish
                  <input type="date" value={row.Est_Done_Date} onChange={(e) => set(item.Item_Id, "Est_Done_Date", e.target.value)} />
                  {item.Due_Date ? <span className="wk-field__hint">Due {fmtShortDate(item.Due_Date)}</span> : null}
                </label>
                <label className="wk-field">
                  Expected post date
                  <input type="date" value={row.Est_Post_Date} onChange={(e) => set(item.Item_Id, "Est_Post_Date", e.target.value)} />
                  {item.Planned_Post_Date && row.Est_Post_Date > item.Planned_Post_Date ? (
                    <span className="wk-field__hint" style={{ color: "#b45309" }}>
                      Later than planned ({fmtShortDate(item.Planned_Post_Date)}); your lead will be told.
                    </span>
                  ) : null}
                </label>
              </div>
              <label className="wk-field" style={{ marginBottom: 0 }}>
                What did you do / what is pending?
                <input value={row.Note} onChange={(e) => set(item.Item_Id, "Note", e.target.value)} placeholder="Short note (optional)" />
              </label>
            </div>
          );
        })
      ) : (
        <p>No open items assigned to you.</p>
      )}
      <label className="wk-field">
        Anything else today? (optional)
        <textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Meetings, leave, other work…" />
      </label>
    </Modal>
  );
}

export default DailyUpdate;
